import { Button } from "@progress/kendo-react-buttons";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  SharedTooltipContext,
} from "@progress/kendo-react-charts";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { useInternationalization } from "@progress/kendo-react-intl";
import { Error } from "@progress/kendo-react-labels";
import { Card, CardBody, CardSubtitle } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { createRef, useCallback, useEffect, useState } from "react";
import contractsApi from "../../api/contracts";
import { useApi } from "../../hooks/useApi";
import DateUtility from "../../utilities/dateUtilities";
import { LoadingIndicator } from "../LoadingIndicator";

interface PromptContractsChartProps {
  commodity: any | null;
  refresh?: number;
}

export const PromptContractsChart = ({ commodity, refresh }: PromptContractsChartProps) => {
  const decimals = commodity?.decimals ?? 2;
  const currencyFormat = `c${decimals}`;
  const unit = (commodity?.units ?? "s").slice(0, -1);
  const formatter = useInternationalization();

  const getPromptContractDataApi = useApi(contractsApi.getPromptContractData);

  const [commodityId, setCommodityId] = useState(0);
  const [curveData, setCurveData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCommodityId(commodity?.id ?? 0);
  }, [commodity]);

  useEffect(() => {
    getData(commodityId);
  }, [commodityId, refresh]);

  useEffect(() => {
    if (getPromptContractDataApi.data?.data)
      setCurveData(formatCurveData(getPromptContractDataApi.data.data));
  }, [getPromptContractDataApi.data]);

  useEffect(() => {
    setIsLoading(getPromptContractDataApi.loading);
  }, [getPromptContractDataApi.loading]);

  const changeYear = (dateStr: string, newYear: number): Date => {
    const originalDate = new Date(dateStr);
    return new Date(newYear, originalDate.getMonth(), originalDate.getDate());
  };

  const formatCurveData = (data: any[]) => {
    if (!data) return [];

    return Object.fromEntries(
      Object.entries(data).map(([year, entries]) => [
        year,
        entries.map((entry: { markDate: string }) => ({
          ...entry,
          chartDate: changeYear(entry.markDate, 2020), // Faking the year, gets the data to plot together
        })),
      ]),
    );
  };

  const getData = useCallback(async (commodityId: number) => {
    if (commodityId > 0) {
      getPromptContractDataApi.request(commodityId);
    }
  }, []);

  const SharedTooltip = (props: SharedTooltipContext) => {
    const { categoryText, points } = props;

    return (
      <div>
        <div style={{ textAlign: "center" }}>
          {DateUtility.formatDateMD(categoryText as string)}
        </div>
        {points.map((point, i) => (
          <div key={i} style={{ display: "flex", gap: "1rem", alignContent: "flex-end" }}>
            <div>{point.series.name}</div>
            <div>
              {formatter.formatNumber(point.value, currencyFormat)} / {unit}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const sharedTooltipRender = (context: any) => <SharedTooltip {...context} />;

  const _exporter = createRef<ExcelExport>();
  const excelExport = () => {
    if (_exporter.current) _exporter.current.save();
  };

  return (
    <>
      <LoadingIndicator loading={isLoading} />

      {commodity && !isLoading && curveData ? (
        <Card>
          <CardBody>
            <CardSubtitle>
              <div
                style={{
                  marginRight: "1rem",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
              >
                <div>Prompt Contract Data</div>
                <Tooltip anchorElement="target" parentTitle={true}>
                  <Button
                    onClick={excelExport}
                    icon="download"
                    size="small"
                    themeColor="primary"
                    title="Export to Excel"
                  ></Button>
                </Tooltip>
              </div>
            </CardSubtitle>

            <Error>{getPromptContractDataApi.error}</Error>

            <Chart transitions={false}>
              <>
                <ChartSeries>
                  {Object.keys(curveData).map((k: string) => (
                    <ChartSeriesItem
                      key={k}
                      name={k}
                      type="line"
                      field="price"
                      categoryField="chartDate"
                      style="smooth"
                      data={curveData[k]}
                      markers={{
                        visible: false,
                      }}
                    />
                  ))}
                </ChartSeries>

                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    labels={{ rotation: "auto", format: "MMM" }}
                    maxDivisions={12}
                  />
                </ChartCategoryAxis>

                <ChartValueAxis>
                  <ChartValueAxisItem
                    labels={{ format: "c2" }}
                    title={{
                      text: `$ / ${unit}`,
                    }}
                  />
                </ChartValueAxis>
              </>
              <ChartTooltip shared={true} render={sharedTooltipRender} />
              <ChartLegend position="right" />
            </Chart>
          </CardBody>

          <ExcelExport
            data={Object.values(curveData).flat()}
            collapsible={true}
            fileName={`PromptContractsData_${commodity.abbreviation}.xlsx`}
            ref={_exporter}
          >
            <ExcelExportColumn field="markDate" title="Mark Date" />
            <ExcelExportColumn field="contract" title="Contract" />
            <ExcelExportColumn field="price" title="Price" />
          </ExcelExport>
        </Card>
      ) : (
        <div>
          {!commodity || isLoading ? "" : "Prompt contract data is unavailable for this commodity"}
        </div>
      )}
    </>
  );
};
